import { Component, Input, Output, EventEmitter } from '@angular/core';
import { RmsModule } from '@rms';
import { WebService, lastValueFrom, RmsMessageService, RmsPulldownService } from '@services';
import { CreateSlackComponent } from './create-slack.component';
import { Create22lbBoxComponent } from './create-22lb-box.component';
import { ConvertSkuComponent } from './convert-sku.component';
import { CompleteWarehouseDateComponent } from './complete-warehouse-date.component';

@Component({
  selector: 'app-inventory-child-actions',
  standalone: true,
  imports: [RmsModule],
  templateUrl: './inventory-child-actions.component.html',
  styleUrl: './inventory-child-actions.component.scss'
})
export class InventoryChildActionsComponent {
  @Input() inventoryItem: any;
  @Output() childInventoryCreated = new EventEmitter<{
    InventoryID: number;
    label: 'Slack' | '22lb Box' | 'Convert SKU';
    BagsOrdered: number;
    NetWeight: number;
    newTypeName?: string;
  } | any>();
  @Output() childInventoryUpdated = new EventEmitter <any>()
  constructor(
    private webService: WebService,
    private message: RmsMessageService,
    private pulldown: RmsPulldownService
  ) { }

  async openSlackDialog(): Promise<void> {
    const reff = this.pulldown.open(CreateSlackComponent, {
      title: 'Create Slack',
      top: '0',
      maxHeight: '98vh',
      data: { ...this.inventoryItem }
    });
    console.log(this.inventoryItem);
    reff.afterClosed().subscribe(async (result: any) => {
      if (!result || result.cancelled) return;

      try {

        /*
         * Update the source inventory.
         *
         * The normal/full bags continue to live against
         * the original InventoryID.
         */
        await lastValueFrom(
          this.webService.getWebCall(
            'inventory',
            'coffee/inventory/merge',
            {
              body: {
                InventoryID: this.inventoryItem.InventoryID,
                overrides: {
                  SlackAdjustment: result.SlackAdjustment
                }
              }
            }
          )
        );


        /*
         * Only create a child when there is actual
         * remaining Slack inventory.
         */
        if ( result.SlackBagsToCreate > 0 &&  result.SlackWeight > 0) {

          const created = await lastValueFrom(
            this.webService.getWebCall(
              'inventory',
              'coffee/inventory/merge',
              {
                body: {
                  InventoryID: this.inventoryItem.InventoryID,
                  createChild: true,
                  overrides: {
                    SlackBag: true,
                    WebInvisible: result.WebInvisible ?? true,

                    BagsOrdered: result.SlackBagsToCreate,

                    BagsInbounded: result.SlackBagsToCreate,

                    NetWeight: result.SlackWeight
                  }
                }
              }
            )
          );

          this.message.success(
            result.SlackBagsToCreate > 0
              ? `${result.FullBagsToCreate} full bag(s) retained and ${result.SlackBagsToCreate} slack bag created.`
              : `${result.FullBagsToCreate} full bag(s) retained.`
          );

          if (created?.InventoryID) {
            this.childInventoryCreated.emit({
              InventoryID: created.InventoryID,
              label: 'Slack',
              BagsOrdered: result.SlackBagsToCreate,
              NetWeight: result.SlackWeight
            });
          }
        }

      } catch (err) {
        console.error(
          'Failed to process slack inventory',
          err
        );

        this.message.error(
          'Something went wrong processing the slack inventory.'
        );
      }
    });
  }
  open22lbBoxDialog(): void {

    const reff = this.pulldown.open(
      Create22lbBoxComponent,
      {
        title: 'Create 22lb Box',
        top: '0',
        data: { ...this.inventoryItem },
        maxHeight: '98vh',
      }
    );

    reff.afterClosed().subscribe(
      async (result: any) => {

        if (!result || result.cancelled) {
          return;
        }

        try {

          /*
           * --------------------------------------
           * CREATE 22LB BOX INVENTORY
           * --------------------------------------
           */
          const createdBoxes =
            await lastValueFrom(
              this.webService.getWebCall(
                'inventory',
                'coffee/inventory/merge',
                {
                  body: {
                    InventoryID:
                      this.inventoryItem.InventoryID,

                    createChild: true,

                    overrides: {
                      SlackBag: true,
                    WebInvisible: result.WebInvisible ?? true,

                      BagsOrdered:
                        result.NumberOfBoxes,

                      BagsInbounded:
                        result.NumberOfBoxes,

                      NetWeight: 22
                    }
                  }
                }
              )
            );





          /*
           * --------------------------------------
           * OPTIONAL SLACK INVENTORY
           * --------------------------------------
           */
          if (
            result.SlackRemaining &&
            result.SlackBagsToCreate > 0 &&
            result.SlackWeight > 0
          ) {

            const createdSlack =
              await lastValueFrom(
                this.webService.getWebCall(
                  'inventory',
                  'coffee/inventory/merge',
                  {
                    body: {
                      InventoryID:
                        this.inventoryItem.InventoryID,

                      createChild: true,

                      overrides: {
                        SlackBag: true,
                    WebInvisible: result.WebInvisible ?? true,

                        BagsOrdered: 1,
                        BagsInbounded: 1,

                        NetWeight:
                          result.SlackWeight
                      }
                    }
                  }
                )
              );

          }


          this.message.success(
            result.SlackRemaining &&
              result.SlackWeight > 0

              ? `Created ${result.NumberOfBoxes} box(es) and 1 slack bag.`

              : `Created ${result.NumberOfBoxes} box(es).`
          );


          if (createdBoxes?.InventoryID) {

            this.childInventoryCreated.emit({
              InventoryID:
                createdBoxes.InventoryID,

              label: '22lb Box',

              BagsOrdered:
                result.NumberOfBoxes,

              NetWeight: 22
            });

          }

        } catch (err) {

          console.error(
            'Failed to create 22lb box inventory',
            err
          );

          this.message.error(
            'Something went wrong creating the box inventory.'
          );
        }

      }
    );
  }
  openConvertSkuDialog(): void {
    const reff = this.pulldown.open(ConvertSkuComponent, {
      title: 'Convert SKU',
      top: '0',
      maxHeight: '97vh',
      data: { ...this.inventoryItem }
    });

    reff.afterClosed().subscribe(async (result: any) => {
      if (!result || result.cancelled) return;

      try {
        const created = await lastValueFrom(
          this.webService.getWebCall('inventory', 'coffee/inventory/convert-sku', {
            body: {
              InventoryID: this.inventoryItem.InventoryID,
              InventoryTypeID: result.InventoryTypeID,
              BagsReconciled: result.BagsReconciled,
              ConversionReason: result.ConversionReason,
              WebInvisible: result.WebInvisible ?? true
            }
          })
        );
        this.message.success(`Converted ${result.BagsReconciled} bags to ${result.InventoryTypeName}.`);
        if (created?.InventoryID) {
          this.childInventoryCreated.emit({
            InventoryID: created.InventoryID,
            label: 'Convert SKU',
            BagsOrdered: result.BagsReconciled,
            NetWeight: this.inventoryItem.NetWeight,
            newTypeName: result.InventoryTypeName
          });
        }
      } catch (err: any) {
        console.error('Failed to create SKU conversion inventory', err);
        this.message.error(err?.error?.error || 'Something went wrong converting the SKU.');
      }
    });
  }

  openCompletionDialog(): void {
    const reff = this.pulldown.open(CompleteWarehouseDateComponent, {
      title: 'Date Created In The Warehouse',
      center: true,
      width: '360px',
      maxWidth: '90vw',
      data: { ...this.inventoryItem }
    });

    reff.afterClosed().subscribe(async (result: any) => {
      if (!result || result.cancelled) return;

      try {
        await lastValueFrom(
          this.webService.getWebCall('inventory', 'coffee/inventory/merge', {
            body: {
              InventoryID: this.inventoryItem.InventoryID,
              overrides: {
                ProcessedDate: result.DateIn
              }
            }
          })
        );
        this.childInventoryUpdated.emit();
      } catch (err) {
        console.error('Failed to update date created in the warehouse', err);
        this.message.error('Something went wrong updating the date.');
      }
    });
  }
}
